/**
 * 지형 타일 / 사다리 / 밧줄 / 포탈 / 배경 오브젝트 도트 아트.
 *
 * 타일은 가로(혹은 세로)로 반복되도록 디자인했고, 맵마다 다른 분위기는
 * MapPalette 의 tileTint / decorTint 로 보정한다.
 */

import { blank, ellipse, line, outline, px, rect, roundRect, type Grid } from './grid';
import type { Palette } from './grid';

export const TERRAIN_PALETTE: Palette = {
  l: '#a6ef78', // 잔디 하이라이트
  n: '#79d45a', // 잔디
  N: '#4f9c3e', // 잔디 음영
  b: '#c08a52', // 흙
  B: '#8f6236', // 흙 음영
  o: '#4a3016',
  x: '#ffffff',
  u: '#7fd0ff',
  U: '#3f86d0',
  w: '#e8f6ff',
  g: '#c08a52',
  G: '#8f6236',
  r: '#e8503c',
  R: '#a52d28',
  m: '#f7ecd2',
  M: '#d6c5a2',
  d: '#6a4a2a',
  t: '#4a8f3c',
  T: '#2f6b2a',
  y: '#ffd24a',
  k: '#8a5a2a',
};

/** 공중 발판 (가로 반복) */
export function platformTile(): Grid {
  const g = blank(16, 10);
  rect(g, 0, 0, 16, 1, 'l');
  rect(g, 0, 1, 16, 2, 'n');
  rect(g, 0, 3, 16, 1, 'N');
  rect(g, 0, 4, 16, 5, 'b');
  rect(g, 0, 9, 16, 1, 'B');
  px(g, 3, 5, 'B');
  px(g, 10, 6, 'B');
  px(g, 13, 5, 'B');
  px(g, 6, 7, 'B');
  px(g, 2, 0, 'n');
  px(g, 9, 0, 'n');
  return g;
}

/** 바닥 지형 표면 (가로 반복) */
export function grassTile(): Grid {
  const g = blank(16, 8);
  rect(g, 0, 0, 16, 1, 'l');
  rect(g, 0, 1, 16, 3, 'n');
  rect(g, 0, 4, 16, 1, 'N');
  rect(g, 0, 5, 16, 3, 'b');
  px(g, 1, 0, 'n');
  px(g, 7, 0, 'n');
  px(g, 12, 0, 'n');
  px(g, 4, 6, 'B');
  px(g, 11, 6, 'B');
  return g;
}

/** 바닥 지형 내부 (가로/세로 반복) */
export function soilTile(): Grid {
  const g = blank(16, 16);
  rect(g, 0, 0, 16, 16, 'b');
  const specks: [number, number][] = [
    [2, 2],
    [9, 3],
    [13, 6],
    [5, 8],
    [11, 11],
    [3, 13],
    [7, 5],
    [14, 14],
  ];
  specks.forEach(([x, y]) => {
    px(g, x, y, 'B');
    px(g, x + 1, y, 'B');
  });
  rect(g, 0, 7, 16, 1, 'B');
  return g;
}

/** 사다리 (세로 반복) */
export function ladderTile(): Grid {
  const g = blank(14, 16);
  rect(g, 1, 0, 3, 16, 'g');
  rect(g, 10, 0, 3, 16, 'g');
  rect(g, 3, 0, 1, 16, 'G');
  rect(g, 12, 0, 1, 16, 'G');
  rect(g, 0, 2, 14, 3, 'k');
  rect(g, 0, 4, 14, 1, 'G');
  rect(g, 0, 10, 14, 3, 'k');
  rect(g, 0, 12, 14, 1, 'G');
  return outline(g);
}

/** 밧줄 (세로 반복) */
export function ropeTile(): Grid {
  const g = blank(8, 16);
  for (let y = 0; y < 16; y++) {
    const phase = y % 8;
    const cx = phase < 4 ? 3 : 4;
    px(g, cx, y, 'g');
    px(g, cx + 1, y, 'G');
    if (phase === 0 || phase === 4) {
      px(g, cx - 1, y, 'G');
      px(g, cx + 2, y, 'g');
    }
  }
  return outline(g);
}

/** 포탈 (4프레임 루프) */
export function portalFrames(): Grid[] {
  return [0, 1, 2, 3].map((f) => {
    const g = blank(28, 36);
    ellipse(g, 14, 33, 10, 3, 'U');
    ellipse(g, 14, 33, 7, 2, 'u');
    for (let k = 0; k < 4; k++) {
      const y = 31 - ((k * 8 + f * 2) % 32);
      const rx = 3 + (31 - y) * 0.22;
      ellipse(g, 14, y, rx, 1.6, k % 2 === 0 ? 'w' : 'u');
      ellipse(g, 14, y, rx * 0.5, 0.9, 'x');
    }
    px(g, 8, 20, 'w');
    px(g, 20, 14, 'w');
    px(g, 11, 8, 'w');
    return g;
  });
}

export function treeArt(): Grid {
  const g = blank(40, 52);
  rect(g, 17, 30, 6, 22, 'd');
  rect(g, 21, 30, 2, 22, 'o');
  line(g, 17, 36, 12, 32, 'd');
  line(g, 23, 34, 28, 30, 'd');
  ellipse(g, 20, 18, 15, 13, 't');
  ellipse(g, 11, 24, 9, 7, 't');
  ellipse(g, 29, 23, 9, 7, 't');
  ellipse(g, 20, 10, 10, 6, 'n');
  ellipse(g, 14, 15, 5, 3, 'n');
  ellipse(g, 26, 26, 6, 3, 'T');
  ellipse(g, 20, 28, 9, 3, 'T');
  return outline(g);
}

export function bushArt(): Grid {
  const g = blank(28, 16);
  ellipse(g, 9, 10, 8, 6, 't');
  ellipse(g, 19, 11, 8, 5, 't');
  ellipse(g, 14, 7, 7, 5, 'n');
  ellipse(g, 8, 6, 4, 2.4, 'l');
  rect(g, 0, 14, 28, 2, 'T');
  return outline(g);
}

export function flowerArt(): Grid {
  const g = blank(12, 14);
  rect(g, 5, 6, 2, 8, 't');
  px(g, 4, 9, 'n');
  px(g, 8, 8, 'n');
  ellipse(g, 6, 4, 3.4, 3.4, 'r');
  ellipse(g, 6, 4, 1.4, 1.4, 'y');
  return outline(g);
}

export function signpostArt(): Grid {
  const g = blank(22, 28);
  rect(g, 9, 10, 4, 18, 'd');
  roundRect(g, 1, 2, 20, 10, 1, 'k');
  rect(g, 3, 4, 16, 6, 'm');
  rect(g, 5, 6, 12, 1, 'M');
  rect(g, 5, 8, 8, 1, 'M');
  return outline(g);
}

/** 원작 마을의 상징인 버섯 모양 집 */
export function mushroomHouseArt(): Grid {
  const g = blank(64, 60);
  // 몸체
  roundRect(g, 14, 26, 36, 34, 3, 'm');
  rect(g, 14, 56, 36, 4, 'M');
  // 문
  roundRect(g, 26, 38, 12, 22, 5, 'k');
  roundRect(g, 28, 41, 8, 19, 4, 'd');
  px(g, 34, 50, 'y');
  // 창문
  ellipse(g, 20, 34, 3.4, 3.4, 'u');
  ellipse(g, 44, 34, 3.4, 3.4, 'u');
  // 갓
  ellipse(g, 32, 22, 31, 20, 'r');
  rect(g, 1, 22, 62, 5, 'r');
  rect(g, 2, 26, 60, 2, 'R');
  ellipse(g, 18, 12, 6, 3.4, 'm');
  ellipse(g, 44, 14, 5, 3, 'm');
  ellipse(g, 32, 6, 4.4, 2.6, 'm');
  ellipse(g, 52, 21, 4, 2.4, 'm');
  ellipse(g, 12, 20, 3.4, 2, 'm');
  return outline(g);
}

export function bigMushroomArt(): Grid {
  const g = blank(34, 30);
  roundRect(g, 12, 14, 10, 16, 2, 'm');
  rect(g, 12, 27, 10, 3, 'M');
  ellipse(g, 17, 11, 16, 9, 'r');
  rect(g, 1, 11, 32, 3, 'r');
  rect(g, 2, 13, 30, 2, 'R');
  ellipse(g, 10, 6, 4, 2.4, 'm');
  ellipse(g, 24, 8, 3.4, 2, 'm');
  ellipse(g, 17, 3, 3, 1.6, 'm');
  return outline(g);
}

export function cloudArt(variant: number): Grid {
  const g = blank(48, 20);
  if (variant === 0) {
    ellipse(g, 14, 12, 12, 7, 'x');
    ellipse(g, 28, 10, 13, 8, 'x');
    ellipse(g, 38, 13, 9, 6, 'x');
  } else {
    ellipse(g, 12, 13, 10, 6, 'x');
    ellipse(g, 24, 9, 11, 8, 'x');
    ellipse(g, 36, 12, 11, 7, 'x');
    ellipse(g, 44, 14, 4, 4, 'x');
  }
  return g;
}
